import React, { useState } from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import "./LogIn.css";
import "../../forms/form-field.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock } from "@fortawesome/free-solid-svg-icons";
import { faEnvelope, faEye, faEyeSlash } from "@fortawesome/free-regular-svg-icons";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLogin } from "../../hooks/useLogin";
import { InputGroup } from "react-bootstrap";
import Modal from "react-modal";
import FormField from "../../forms/FormField";
import { loginSchema } from "../../forms/schemas";

// Establece ontenedor de Modal
Modal.setAppElement("#root");

const REMEMBER_EMAIL_KEY = "RemenberEmail";

/**
 * Lee el email recordado sin reventar si localStorage no esta disponible
 * (modo privado de algunos navegadores, o storage deshabilitado).
 */
const readRememberedEmail = () => {
  try {
    return localStorage.getItem(REMEMBER_EMAIL_KEY) || "";
  } catch (error) {
    return "";
  }
};

function LogIn(props) {
  const [showPassword, setShowPassword] = useState(false);
  const { logIn } = useLogin();
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState(false); // Estado para abrir el Modal
  const [modalMessage, setModalMessage] = useState(""); // Mensaje del Modal

  // KHAN-146: react-hook-form + Zod reemplazan el useState por campo y, sobre
  // todo, el useEffect sin arreglo de dependencias que corria en CADA render
  // escribiendo estilos con document.getElementById. El email recordado ahora
  // entra como defaultValue en vez de inyectarse al DOM despues del montaje.
  const rememberedEmail = readRememberedEmail();
  const {
    register,
    handleSubmit,
    resetField,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(loginSchema),
    mode: "onTouched",
    defaultValues: {
      email: rememberedEmail,
      password: "",
      remember: Boolean(rememberedEmail),
    },
  });

  const email = watch("email");
  const password = watch("password");
  // Mismo criterio visual que antes (boton "lleno" cuando hay datos en ambos
  // campos), pero derivado del estado del formulario en vez de escrito a mano
  // sobre el DOM.
  const submitClassName =
    email && password ? "login-form-button-filled" : "login-form-button-empty";

  const togglePassword = () => setShowPassword((visible) => !visible);

  const showError = (message) => {
    resetField("password");
    setModalMessage(message);
    setIsModalOpen(true);
  };

  /**
   * Se ejecuta solo cuando Zod dio por validos los campos; recibe los valores ya
   * parseados, asi que no hace falta leerlos del DOM ni del estado local.
   */
  const onSubmit = async ({ email: emailValue, password: passwordValue, remember }) => {
    sessionStorage.clear();
    try {
      if (remember) {
        localStorage.setItem(REMEMBER_EMAIL_KEY, emailValue);
      } else {
        localStorage.removeItem(REMEMBER_EMAIL_KEY);
      }
    } catch (error) {
      // localStorage puede no estar disponible; no bloquea el login.
    }

    try {
      // useLogin returns either roles (array) on success, "noRoles" string,
      // or a string with an error detail when login fails.
      const res = await logIn(emailValue, passwordValue);

      if (res === "noRoles") {
        navigate("/register");
        return;
      }

      if (Array.isArray(res)) {
        // Successful login: roles returned -> go to dashboard
        navigate("/dashboard");
        return;
      }

      // Any other non-array response is treated as an error detail string
      showError(
        typeof res === "string" && res.length
          ? res
          : "Please verify your credentials."
      );
    } catch (e) {
      // Fallback: show generic message if something unexpected happened
      showError("An unexpected error occurred. Please try again.");
    }
  };

  return (
    <div className="login-form">
      <div className="login-form-body">
        <Form
          onSubmit={handleSubmit(onSubmit)}
          className="login-form-content"
          noValidate
        >
          <h1 className="login-form-title">Welcome Back!</h1>
          <h5 className="login-form-subtitle">
            Add your information to Log In.
          </h5>
          <div className="login-form-inputs">
            <FormField
              id="emailInput"
              label="Email"
              icon={<FontAwesomeIcon className="login-icons" icon={faEnvelope} />}
              type="email"
              placeholder="example@mep.co.cr"
              className="login-form-control-lg"
              error={errors.email}
              registration={register("email")}
            />
            <FormField
              id="passwordInput"
              label="Password"
              icon={<FontAwesomeIcon className="login-icons" icon={faLock} />}
              type={showPassword ? "text" : "password"}
              placeholder="Your password"
              className="login-form-control-lg"
              style={{ borderRightWidth: 0 }}
              error={errors.password}
              registration={register("password")}
            >
              {(control) => (
                <InputGroup className="form-input-group">
                  {control}
                  <InputGroup.Text
                    id="inputGroupText"
                    className="form-input-group-text-password"
                  >
                    <Button
                      id="passwordButton"
                      className="login-form-password-button"
                      onClick={togglePassword}
                      type="button"
                      aria-label={
                        showPassword ? "Ocultar contrasena" : "Mostrar contrasena"
                      }
                    >
                      {showPassword ? (
                        <FontAwesomeIcon className="fa-xl float end" icon={faEye} />
                      ) : (
                        <FontAwesomeIcon
                          className="fa-xl float end"
                          icon={faEyeSlash}
                        />
                      )}
                    </Button>
                  </InputGroup.Text>
                </InputGroup>
              )}
            </FormField>
            <Form.Group className="form-group">
              <div className="mb-3 form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="rememberMeCheck"
                  {...register("remember")}
                />
                <label htmlFor="rememberMeCheck">Remember me</label>
              </div>
            </Form.Group>
            <div style={{ textAlign: "right", marginBottom: 8 }}>
              <span
                className="font-size"
                style={{ cursor: "pointer", textDecoration: "underline" }}
                onClick={() => navigate("/forgot-password")}
              >
                Forgot your password?
              </span>
            </div>
            <Button
              id="submit-button"
              className={submitClassName}
              type="submit"
              disabled={isSubmitting}
            >
              Log In
            </Button>
          </div>
        </Form>
      </div>

      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        contentLabel="Error Modal"
        className="modal-content"
        overlayClassName="modal-overlay"
      >
        <div className="modal-header">
          <h2>Alert</h2>
        </div>
        <div className="modal-body">
          <p>{modalMessage}</p>
        </div>
        <div className="modal-footer">
          <Button variant="primary" onClick={() => setIsModalOpen(false)}>
            Close
          </Button>
        </div>
      </Modal>
    </div>
  );
}

export default LogIn;
